import { WebCodecsCapabilities } from '../client/WebCodecsCapabilities';
import type { WcWorkerInput, WcWorkerOutput, WcWorkerPlatform } from '../client/WebCodecsClientTypes';
import type { WcConfig, WcControl } from '../protocol/WebCodecsTypes';
import { WebCodecsWire } from '../protocol/WebCodecsWire';
import { FrameReassembler } from './FrameReassembler';
import { WebCodecsDecoder } from './WebCodecsDecoder';
import { WebCodecsRenderer } from './WebCodecsRenderer';
import { WebCodecsFeedback, type WcReceiveSnapshot } from './WebCodecsFeedback';

/** Worker orchestrator; platforms and output dispatch are injected, with no browser globals in tests. */
export class WebCodecsWorkerRuntime {
  private session = '';
  private closed = false;
  private visible = true;
  private generation = 0;
  private requestedEpoch = 0;
  private config: WcConfig | null = null;
  private renderer: WebCodecsRenderer | null = null;
  private decoder: WebCodecsDecoder | null = null;
  private reassembler: FrameReassembler | null = null;
  private feedback: WebCodecsFeedback | null = null;
  private timer: number | null = null;
  private chain = Promise.resolve();
  private queuedControls = 0;
  private keyNeeded = true;
  private lastKeyRequestUs = -Infinity;
  private firstPresented = false;
  private configuredUs = 0;
  private stallOriginUs = 0;
  private hiddenCarryUs = 0;
  private hiddenAtUs: number | null = null;
  private stallAnnounced = false;
  private keyUnansweredSinceUs: number | null = null;
  private keyHiddenUs = 0;
  private readonly arrivals = new Map<number, number>();
  /** Sender clock of each frame handed to the decoder, kept until it is painted or dropped (latency reports). */
  private readonly sentUsById = new Map<number, number>();
  private static readonly SENT_MAX_ENTRIES = 64;
  /** Fragments of an epoch not yet configured, held until its Configure is processed (see fragment()). */
  private held: Array<{ epoch: number; bytes: ArrayBuffer; receivedUs: number }> = [];
  private heldBytes = 0;
  private ingressReplay = false;
  private static readonly HOLD_MAX_BYTES = 256 * 1024;
  private static readonly HOLD_MAX_AGE_US = 1_000_000;
  private static readonly CLOCK_SKEW_MAX_US = 1_000_000;
  constructor(private readonly platform: WcWorkerPlatform, private readonly emit: (message: WcWorkerOutput) => void) {}
  receive(input: WcWorkerInput): void {
    if (input.type === 'close') { this.close(); return; }
    if (this.closed) return;
    try {
      if (input.type === 'start') this.start(input.session, input.canvas);
      if (input.type === 'visibility') this.visibility(input.visible);
      if (input.type === 'fragment') this.fragment(input.bytes, input.receivedUs);
      if (input.type === 'restart') this.restartDecoder();
      if (input.type === 'control') this.enqueueControl(input.message);
    } catch (error) { this.fail(`Worker input failed: ${error instanceof Error ? error.message : 'unknown platform error'}`); }
  }
  close(): void {
    if (this.closed) return;
    this.closed = true; ++this.generation;
    if (this.timer !== null) this.platform.clearTimer(this.timer);
    this.timer = null; this.decoder?.close(); this.renderer?.close(); this.reassembler?.close();
  }
  private start(session: string, canvas: OffscreenCanvas): void {
    if (this.session) throw new Error('Worker sessions cannot be reused');
    WebCodecsWire.EncodeControl({ v: 1, type: 'close', session, reason: '' }); this.session = session;
    this.renderer = new WebCodecsRenderer(this.platform.createSurface(canvas), this.platform, {
      presented: (id, interval) => {
        if (this.closed || !this.config || !this.renderer) return;
        // The renderer reports synchronously from its paint, so this clock reading is the draw time.
        const paintedAtUs = this.platform.nowUs(), sentUs = this.sentUsById.get(id); this.sentUsById.delete(id);
        this.feedback?.presentedFrame(id, interval);
        this.emit({ type: 'presented', epoch: this.config.epoch, presentedFrames: this.renderer.presentedCount(),
          maxGapMs: this.renderer.maxPresentGapMs(), latencyUs: this.renderer.drainLatencySamplesUs() });
        this.emit({ type: 'event', event: { type: 'presented', epoch: this.config.epoch, frameId: id, first: !this.firstPresented,
          ...(sentUs === undefined ? {} : { sentUs, paintedAtUs }) } });
        this.firstPresented = true;
      },
      dropped: id => { this.sentUsById.delete(id); this.feedback?.droppedFrame(id); }, failed: reason => this.fail(reason),
    });
    this.renderer.setVisible(this.visible); this.schedule();
  }
  private enqueueControl(message: WcControl): void {
    const bytes = WebCodecsWire.EncodeControl(message).byteLength;
    if (message.session !== this.session) { this.emit({ type: 'ingressReleased', bytes }); return; }
    if (++this.queuedControls > 32) throw new Error('Control queue exceeded');
    // Retire old async support probes immediately, before their promises resolve.
    if (message.type === 'configure' && message.config.epoch > this.requestedEpoch) {
      const replaced = this.config?.epoch;
      this.requestedEpoch = message.config.epoch; ++this.generation;
      this.decoder?.close(); this.renderer?.clear(); this.reassembler?.close(); this.config = null;
      if (replaced) this.emit({ type: 'needsKeyframe', epoch: message.config.epoch, reason: 'epoch' });
    }
    this.chain = this.chain.then(async () => { if (!this.closed) await this.control(message); })
      .catch(() => this.fail('Worker control transition failed'))
      .finally(() => { --this.queuedControls; this.emit({ type: 'ingressReleased', bytes }); });
  }
  private async control(message: WcControl): Promise<void> {
    if (message.type === 'configure') { await this.configure(message.config); return; }
    if (message.type === 'close') { this.close(); return; }
    if (message.type === 'ping') {
      const clientReceiveUs = this.platform.nowUs();
      this.send({ v: 1, type: 'pong', session: this.session, probeId: message.probeId, hostSendUs: message.hostSendUs,
        clientReceiveUs, clientSendUs: this.platform.nowUs() }); return;
    }
    if (message.type === 'source' && message.epoch === this.config?.epoch)
      this.reassembler?.observeSource(message.latestFrameId, this.platform.nowUs());
  }
  private async configure(config: WcConfig): Promise<void> {
    if (config.epoch !== this.requestedEpoch || config.epoch === this.config?.epoch) return;
    const generation = this.generation;
    const accepted = await new WebCodecsCapabilities(this.platform, this.platform).exact(config);
    if (this.closed || generation !== this.generation) return;
    if (!accepted || !this.renderer) {
      this.send({ v: 1, type: 'rejected', session: this.session, epoch: config.epoch, reason: 'Exact decoder configuration unavailable' }); return;
    }
    this.config = config;
    const feedback = new WebCodecsFeedback(this.session, config.epoch); this.feedback = feedback;
    this.configuredUs = this.platform.nowUs();
    this.keyNeeded = true; this.firstPresented = false; this.lastKeyRequestUs = -Infinity;
    this.renderer.configure(config.width, config.height);
    this.decoder = new WebCodecsDecoder(this.platform, () => this.platform.nowUs(), {
      output: (frame, id, elapsed) => {
        if (this.closed || generation !== this.generation) { frame.close(); return; }
        const arrival = this.arrivals.get(id); this.arrivals.delete(id);
        this.feedback?.decodedFrame(id, elapsed); this.renderer?.accept(frame, id, arrival); this.drain();
      }, failed: reason => this.fail(reason),
      needsKeyframe: () => this.onDecoderKeyframe(),
      unsupported: (codec, message) => { if (!this.closed) this.emit({ type: 'unsupported', codec, message: message.slice(0, 256) }); },
    });
    if (!this.decoder.configure(accepted)) { this.decoder.close(); this.decoder = null; this.config = null; return; }
    this.decoder.setVisible(this.visible);
    this.stallOriginUs = this.configuredUs; this.hiddenCarryUs = 0; this.stallAnnounced = false;
    this.keyUnansweredSinceUs = null; this.keyHiddenUs = 0; this.arrivals.clear(); this.sentUsById.clear();
    this.reassembler = new FrameReassembler(config.epoch, config.fps, {
      reserve: (id, bytes) => feedback.reserve(id, bytes),
      recover: through => this.recover(through, 'gap'),
    }, this.reassembler?.getRepairRttUs() ?? 0); // the path's repair round trip outlives the rendition
    if (this.closed) return;
    this.send({ v: 1, type: 'configured', session: this.session, epoch: config.epoch });
    this.emit({ type: 'event', event: { type: 'configured', config } });
    this.replayHeld(config.epoch);
  }
  private fragment(bytes: ArrayBuffer, receivedUs: number): void {
    try {
      const now = this.platform.nowUs();
      // Window and worker performance clocks share an origin, but each side reduces timer precision on
      // its own (Safari to whole milliseconds, on both now() and timeOrigin), so an immediate delivery can
      // land a few milliseconds ahead. Only a disagreement of a second or more is a wiring error.
      if (!Number.isSafeInteger(receivedUs) || receivedUs < 0 || receivedUs > now + WebCodecsWorkerRuntime.CLOCK_SKEW_MAX_US) throw new Error('Client clock origins differ');
      receivedUs = Math.min(receivedUs, now);
      const fragment = WebCodecsWire.DecodeFrameFragment(new Uint8Array(bytes));
      if (!fragment) return;
      // The host sends an epoch's first key right behind its Configure instead of waiting a round trip
      // for the acknowledgement. The video channel is unordered and the decoder configuration check is
      // asynchronous, so the key can get here first: hold fragments of a newer epoch until it is configured.
      if (fragment.epoch !== this.config?.epoch) { if (fragment.epoch > (this.config?.epoch ?? 0)) this.hold(fragment.epoch, bytes, receivedUs, now); return; }
      if (!this.feedback || !this.reassembler) return;
      if (!this.feedback.arrival(fragment, bytes.byteLength, receivedUs, now)) return;
      this.noteBytes(now);
      if (!this.visible) { this.reassembler.observeSource(fragment.frameId, now); this.reassembler.recover(); return; }
      // Arrival time, not worker dequeue time, starts the reassembly deadline.
      this.reassembler.accept(fragment, receivedUs); this.reassembler.tick(now); this.drain();
      if (this.feedback.hasUnreportedGap()) this.publish(); // NACK the skipped id now rather than on the next timer
    } finally { if (!this.ingressReplay) this.emit({ type: 'ingressReleased', bytes: bytes.byteLength }); } // held bytes were released when first seen
  }
  /** Bounded in bytes and age; the oldest go first, and the hold never outlives a Configure that never comes. */
  private hold(epoch: number, bytes: ArrayBuffer, receivedUs: number, now: number): void {
    this.held = this.held.filter(item => { const keep = now - item.receivedUs <= WebCodecsWorkerRuntime.HOLD_MAX_AGE_US; if (!keep) this.heldBytes -= item.bytes.byteLength; return keep; });
    while (this.held.length && this.heldBytes + bytes.byteLength > WebCodecsWorkerRuntime.HOLD_MAX_BYTES) this.heldBytes -= this.held.shift()!.bytes.byteLength;
    if (bytes.byteLength > WebCodecsWorkerRuntime.HOLD_MAX_BYTES) return;
    this.held.push({ epoch, bytes, receivedUs }); this.heldBytes += bytes.byteLength;
  }
  private replayHeld(epoch: number): void {
    const held = this.held.filter(item => item.epoch >= epoch); this.held = []; this.heldBytes = 0;
    for (const item of held) {
      if (item.epoch === epoch) { this.ingressReplay = true; try { this.fragment(item.bytes, item.receivedUs); } finally { this.ingressReplay = false; } }
      else this.hold(item.epoch, item.bytes, item.receivedUs, this.platform.nowUs());
    }
  }
  private drain(): void {
    if (!this.visible || this.closed || !this.config || !this.decoder || !this.reassembler) return;
    while (this.decoder.canAccept()) {
      const frame = this.reassembler.take(); if (!frame) return;
      if (frame.key) {
        // A keyframe is independently decodable. Rebuilding VideoToolbox here (Safari) drops the
        // frames it was already holding and stalls the next ones until the new decoder warms up.
        this.feedback?.retireThrough(frame.frameId - 1); this.keyNeeded = false;
        this.keyUnansweredSinceUs = null; this.keyHiddenUs = 0;
      }
      this.arrivals.set(frame.frameId, frame.receivedUs);
      this.rememberSent(frame.frameId, frame.sentUs);
      if (!this.decoder.submit(frame)) { this.arrivals.delete(frame.frameId); this.sentUsById.delete(frame.frameId); this.reassembler.recover(); return; }
    }
  }
  /** A frame the decoder never returns (reset, error) would otherwise pin its entry; the oldest go first. */
  private rememberSent(frameId: number, sentUs: number): void {
    this.sentUsById.set(frameId, sentUs);
    while (this.sentUsById.size > WebCodecsWorkerRuntime.SENT_MAX_ENTRIES) {
      const oldest = this.sentUsById.keys().next().value;
      if (oldest === undefined) break;
      this.sentUsById.delete(oldest);
    }
  }
  private recover(through: number, reason: 'gap' | 'decode-error' | 'epoch' = 'gap'): void {
    this.decoder?.reset(); this.renderer?.clear(); this.feedback?.retireThrough(through); this.keyNeeded = true; this.requestKey();
    if (this.config && !this.closed) this.emit({ type: 'needsKeyframe', epoch: this.config.epoch, reason });
  }
  private requestKey(): void {
    const now = this.platform.nowUs();
    if (!this.config || !this.visible || now - this.lastKeyRequestUs < 200000) return;
    // A key already landing is mended by resends; a fresh request would only make the host discard it.
    if (this.reassembler?.assemblingKey()) return;
    this.lastKeyRequestUs = now;
    if (this.keyUnansweredSinceUs === null) this.keyUnansweredSinceUs = now;
    this.send({ v: 1, type: 'keyframe', session: this.session, epoch: this.config.epoch, afterFrameId: this.feedback?.getRetiredThrough() ?? 0 });
  }
  private visibility(visible: boolean): void {
    if (visible === this.visible) return;
    const now = this.platform.nowUs();
    if (!visible && this.hiddenAtUs === null) this.hiddenAtUs = now;
    if (visible && this.hiddenAtUs !== null) {
      const hiddenStart = this.hiddenAtUs;
      this.hiddenCarryUs += now - hiddenStart;
      if (this.keyUnansweredSinceUs !== null) this.keyHiddenUs += now - Math.max(hiddenStart, this.keyUnansweredSinceUs);
      this.hiddenAtUs = null;
    }
    this.visible = visible; this.renderer?.setVisible(visible); this.decoder?.setVisible(visible);
    this.reassembler?.recover();
    if (visible) { this.lastKeyRequestUs = -Infinity; this.requestKey(); }
    this.publish();
  }
  private schedule(): void {
    this.timer = this.platform.setTimer(() => {
      this.timer = null; if (this.closed) return;
      try {
        const now = this.platform.nowUs();
        this.reassembler?.tick(now); this.decoder?.tick(); this.drain();
        if (this.keyNeeded && (this.lastKeyRequestUs !== -Infinity || now - this.configuredUs >= 200000)) this.requestKey();
        this.watchStall(now);
        this.publish(); if (!this.closed) this.schedule();
      } catch (error) { this.fail(`Receiver budget or feedback failure: ${error instanceof Error ? error.message : 'unknown'}`); }
    }, 5);
  }
  private publish(): void {
    if (!this.feedback || this.closed) return;
    // A frame still being reassembled is being paced by the host on purpose (a keyframe on a slow
    // path may take most of a second); it is never receiver backlog. The reassembler bounds it with
    // its own progress deadline. Decode and presentation ages are real receiver backlog.
    const snapshot: WcReceiveSnapshot = { reassemblyBytes: this.reassembler?.getBytes() ?? 0, decodeQueueSize: this.decoder?.getQueueSize() ?? 0,
      decoderInFlight: this.decoder?.getInFlight() ?? 0, renderPending: this.renderer?.getPending() ?? 0,
      oldestPendingAgeUs: Math.max(this.decoder?.oldestAgeUs() ?? 0, this.renderer?.oldestAgeUs() ?? 0),
      missingFragments: this.reassembler?.missing(this.platform.nowUs()) ?? [], visible: this.visible,
      needsKeyframe: this.keyNeeded && !this.reassembler?.assemblingKey() };
    const message = this.feedback.report(this.platform.nowUs(), snapshot, this.reassembler?.missing(this.platform.nowUs(), true) ?? []);
    if (message) {
      this.emit({ type: 'feedback', message });
      const reassembly = { repairHoldUs: this.reassembler?.getRepairHoldUs() ?? 0, keyAsks: this.reassembler?.getKeyAsks() ?? 0 };
      this.emit({ type: 'stats', stats: this.feedback.stats(snapshot, reassembly) });
    }
  }
  private noteBytes(now: number): void {
    this.stallOriginUs = now; this.hiddenCarryUs = 0; this.stallAnnounced = false;
    if (this.hiddenAtUs !== null) this.hiddenAtUs = now;
  }
  private quietUs(now: number): number {
    const pending = this.hiddenAtUs === null ? 0 : Math.max(0, now - this.hiddenAtUs);
    return Math.max(0, now - this.stallOriginUs - this.hiddenCarryUs - pending);
  }
  /** Emits `stalled` only after 3s without bytes and 1s with an unanswered keyframe. Hidden time does not count. */
  private watchStall(now: number): void {
    if (!this.config || !this.visible || this.stallOriginUs === 0) return;
    const quiet = this.quietUs(now);
    if (quiet >= 1_000_000 && !this.keyNeeded) { this.keyNeeded = true; this.lastKeyRequestUs = -Infinity; this.requestKey(); }
    const pendingHidden = this.hiddenAtUs === null || this.keyUnansweredSinceUs === null ? 0
      : Math.max(0, now - Math.max(this.hiddenAtUs, this.keyUnansweredSinceUs));
    const keyWait = this.keyUnansweredSinceUs === null ? 0 : Math.max(0, now - this.keyUnansweredSinceUs - this.keyHiddenUs - pendingHidden);
    if (quiet >= 3_000_000 && keyWait >= 1_000_000) {
      if (!this.stallAnnounced) {
        this.stallAnnounced = true;
        this.emit({ type: 'stalled', epoch: this.config.epoch, sinceMs: Math.round(quiet / 1000) });
      }
    } else if (quiet < 3_000_000) this.stallAnnounced = false;
  }
  private onDecoderKeyframe(): void {
    if (this.closed || !this.config) return;
    this.keyNeeded = true; this.lastKeyRequestUs = -Infinity; this.requestKey();
    this.emit({ type: 'needsKeyframe', epoch: this.config.epoch, reason: 'decode-error' });
  }
  private restartDecoder(): void {
    if (this.closed || !this.config) return;
    this.decoder?.reset(); this.keyNeeded = true; this.lastKeyRequestUs = -Infinity; this.requestKey();
  }
  private send(message: WcControl): void { if (!this.closed) this.emit({ type: 'control', message }); }
  private fail(reason: string): void { if (!this.closed) { this.close(); this.emit({ type: 'event', event: { type: 'failed', reason: reason.slice(0, 256) } }); } }
}
